package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentGreen
import com.rainbowclubs.rainbow.presentation.theme.accentPurple
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.back_icon_cd
import rainbowclubs.composeapp.generated.resources.calendar_icon_cd
import rainbowclubs.composeapp.generated.resources.chevron_icon_cd
import rainbowclubs.composeapp.generated.resources.create_event_icon_cd
import rainbowclubs.composeapp.generated.resources.create_new_event
import rainbowclubs.composeapp.generated.resources.create_new_event_description
import rainbowclubs.composeapp.generated.resources.edit_event_icon_cd
import rainbowclubs.composeapp.generated.resources.edit_existing_event
import rainbowclubs.composeapp.generated.resources.edit_existing_event_description
import rainbowclubs.composeapp.generated.resources.event_actions
import rainbowclubs.composeapp.generated.resources.event_actions_icon_cd
import rainbowclubs.composeapp.generated.resources.ic_arrow_back
import rainbowclubs.composeapp.generated.resources.ic_calendar
import rainbowclubs.composeapp.generated.resources.ic_chevron_right
import rainbowclubs.composeapp.generated.resources.ic_dashboard_grid
import rainbowclubs.composeapp.generated.resources.ic_document_edit
import rainbowclubs.composeapp.generated.resources.manage_events
import rainbowclubs.composeapp.generated.resources.manage_events_page_description
import rainbowclubs.composeapp.generated.resources.select_from_list
import rainbowclubs.composeapp.generated.resources.start_fresh

sealed class ManageEventsNavigationEvent {
    object Back : ManageEventsNavigationEvent()

    data class EditExistingEvent(val clubId: String) : ManageEventsNavigationEvent()

    data class CreateNewEvent(val clubId: String) : ManageEventsNavigationEvent()
}

@Composable
fun ManageEventsScreen(
    clubId: String,
    navigateTo: (ManageEventsNavigationEvent) -> Unit,
) {
    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            ManageEventsScreenContent(
                clubId = clubId,
                navigateTo = navigateTo,
            )
        }
    }
}

@Composable
private fun ManageEventsScreenContent(
    clubId: String,
    navigateTo: (ManageEventsNavigationEvent) -> Unit,
) {
    val actions = manageEventActions(
        onEditExistingEventClick = {
            navigateTo(ManageEventsNavigationEvent.EditExistingEvent(clubId))
        },
        onCreateNewEventClick = {
            navigateTo(ManageEventsNavigationEvent.CreateNewEvent(clubId))
        },
    )

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        ManageEventsHeader(
            onBackClick = {
                navigateTo(ManageEventsNavigationEvent.Back)
            }
        )

        Spacer(modifier = Modifier.height(52.dp))

        Row(
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_dashboard_grid),
                contentDescription = stringResource(Res.string.event_actions_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(modifier = Modifier.width(16.dp))
            Text(
                text = stringResource(Res.string.event_actions),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
            )
        }

        Spacer(modifier = Modifier.height(28.dp))

        Column(
            verticalArrangement = Arrangement.spacedBy(24.dp),
        ) {
            actions.forEach { action ->
                ManageEventActionCard(action = action)
            }
        }

        Spacer(modifier = Modifier.height(36.dp))

    }
}

@Composable
private fun ManageEventsHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(
            onClick = onBackClick,
            modifier = Modifier.align(Alignment.Top).size(24.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(16.dp))

        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = stringResource(Res.string.manage_events),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.width(10.dp))
                Icon(
                    painter = painterResource(Res.drawable.ic_calendar),
                    contentDescription = stringResource(Res.string.calendar_icon_cd),
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.primary,
                )
            }

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.manage_events_page_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun ManageEventActionCard(action: ManageEventAction) {
    Surface(
        onClick = action.onClick,
        modifier = Modifier
            .fillMaxWidth(),
        shape = RoundedCornerShape(28.dp),
        color = action.accent.copy(alpha = 0.06f),
        border = BorderStroke(1.dp, action.accent.copy(alpha = 0.18f)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(72.dp)
                    .shadow(
                        elevation = 18.dp,
                        shape = RoundedCornerShape(24.dp),
                        ambientColor = action.accent.copy(alpha = 0.18f),
                        spotColor = action.accent.copy(alpha = 0.18f),
                    )
                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(24.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .size(50.dp)
                        .background(
                            brush = Brush.linearGradient(
                                listOf(action.accent.copy(alpha = 0.82f), action.accent)
                            ),
                            shape = RoundedCornerShape(14.dp),
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        painter = painterResource(action.icon),
                        contentDescription = stringResource(action.iconContentDescription),
                        modifier = Modifier.size(34.dp),
                        tint = MaterialTheme.colorScheme.onPrimary,
                    )
                }
            }

            Spacer(modifier = Modifier.width(28.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(action.title),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(10.dp))

                Text(
                    text = stringResource(action.description),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(10.dp))

                ActionChip(action = action)
            }

            Spacer(modifier = Modifier.width(16.dp))

            Box(
                modifier = Modifier
                    .size(24.dp)
                    .background(action.accent.copy(alpha = 0.12f), RoundedCornerShape(26.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = stringResource(Res.string.chevron_icon_cd),
                    modifier = Modifier.size(24.dp),
                    tint = action.accent,
                )
            }
        }
    }
}

@Composable
private fun ActionChip(action: ManageEventAction) {
    Surface(
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, action.accent.copy(alpha = 0.28f)),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(8.dp)
                    .background(action.accent, RoundedCornerShape(8.dp)),
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = stringResource(action.chipText),
                color = action.accent,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun manageEventActions(
    onEditExistingEventClick: () -> Unit,
    onCreateNewEventClick: () -> Unit,
): List<ManageEventAction> {
    val colorScheme = MaterialTheme.colorScheme

    return listOf(
        ManageEventAction(
            title = Res.string.edit_existing_event,
            description = Res.string.edit_existing_event_description,
            chipText = Res.string.select_from_list,
            icon = Res.drawable.ic_document_edit,
            iconContentDescription = Res.string.edit_event_icon_cd,
            accent = colorScheme.accentGreen,
            onClick = onEditExistingEventClick,
        ),
        ManageEventAction(
            title = Res.string.create_new_event,
            description = Res.string.create_new_event_description,
            chipText = Res.string.start_fresh,
            icon = Res.drawable.ic_calendar,
            iconContentDescription = Res.string.create_event_icon_cd,
            accent = colorScheme.accentPurple,
            onClick = onCreateNewEventClick,
        ),
    )
}

private data class ManageEventAction(
    val title: StringResource,
    val description: StringResource,
    val chipText: StringResource,
    val icon: DrawableResource,
    val iconContentDescription: StringResource,
    val accent: Color,
    val onClick: () -> Unit,
)

@Preview(name = "Manage Events Content")
@Composable
private fun ManageEventsScreenContentPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                ManageEventsScreenContent(
                    clubId = "club-id",
                    navigateTo = {},
                )
            }
        }
    }
}

@Preview(name = "Manage Event Action Card")
@Composable
private fun ManageEventActionCardPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                ManageEventActionCard(
                    action = manageEventActions(
                        onEditExistingEventClick = {},
                        onCreateNewEventClick = {},
                    ).first()
                )
            }
        }
    }
}
